import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslocoDirective } from '@jsverse/transloco';
import { ForceUpdateService } from '@shared/lib/force-update/force-update.service';
import { EXTENSION_STORE_URL, IS_EXTENSION } from '@shared/platform';
import { LumIconComponent } from '@shared/ui/icon/lum-icon';

const OVERLAY_Z = 1300;

@Component({
  selector: 'app-force-update-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, LumIconComponent, TranslocoDirective],
  template: `
    @if (outdated()) {
      <div
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        role="alertdialog"
        aria-modal="true"
        [attr.aria-labelledby]="titleId"
        [style.z-index]="OVERLAY_Z"
        class="fixed inset-0 flex items-center justify-center bg-bg-primary px-6"
        *transloco="let t"
      >
        <div class="w-full max-w-[300px] text-center">
          <span
            class="mx-auto mb-4 flex size-12 items-center justify-center rounded-full border border-border bg-bg-elevated text-text-muted"
          >
            <lum-icon name="refresh" [size]="20" />
          </span>

          <h2 [id]="titleId" class="mb-1.5 text-sm font-semibold text-text-primary">
            {{ t('shell.updateRequired') }}
          </h2>
          <p class="text-[13px] leading-relaxed text-text-muted">
            {{ t(hintKey()) }}
          </p>

          @if (storeUrl) {
            <a
              [href]="storeUrl"
              target="_blank"
              rel="noopener"
              class="mt-5 flex h-10 w-full items-center justify-center rounded-[var(--radius-md)] bg-accent text-[13px] font-semibold text-white no-underline transition-colors hover:bg-accent-hover"
            >
              {{ t('shell.updateRequiredAction') }}
            </a>
          }
        </div>
      </div>
    }
  `,
})
export class ForceUpdateOverlay {
  private readonly forceUpdate = inject(ForceUpdateService);

  protected readonly OVERLAY_Z = OVERLAY_Z;
  protected readonly titleId = 'force-update-title';
  protected readonly storeUrl = inject(EXTENSION_STORE_URL);
  private readonly isExtension = inject(IS_EXTENSION);

  protected readonly hintKey = computed(() => {
    if (this.storeUrl) return 'shell.updateRequiredHint';
    return this.isExtension ? 'shell.updateRequiredHintManual' : 'shell.updateRequiredHintWeb';
  });
  protected readonly outdated = computed(() => this.forceUpdate.isOutdated());
}
